import { Clock, Eye, Shield } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { AccessEffectiveCell } from "./AccessEffectiveCell";
import { AccessUsersEmpty } from "./AccessUsersEmpty";
import { type AccessUsersListProps, canOpenRolesFor } from "./accessUsersList.types";
import { PrincipalAvatar } from "./PrincipalAvatar";
import { PrincipalRolesCell } from "./PrincipalRolesCell";

/**
 * Three of the four tracks are fixed lengths, so the grid cannot shrink below 442px, inside an
 * `overflow-hidden` wrapper that does not truncate gracefully — it clips the last columns out of existence.
 * Below the breakpoint the same data is painted by `AccessUsersCards`.
 *
 * The breakpoint is `xl`, not `lg`, and the difference is not cosmetic. `lg` means a 1024px VIEWPORT, but
 * this page concedes far less: the dashboard sidebar takes 256px and the settings sub-nav another 220 before
 * any padding, so the grid only has room from about 1072px up. Between 1024 and 1071 the row lost 48px —
 * enough to delete the row's only action. Pick the breakpoint against the width the page actually grants,
 * never against the viewport.
 */
const GRID_COLS = "grid grid-cols-[1fr_230px_120px_92px] items-center";

/** Desktop face of the users list. Mobile is `AccessUsersCards`; both take `AccessUsersListProps`. */
export function AccessUsersTable({
  users,
  rolesByUser,
  rolesUnavailable,
  summaryById,
  catalogUnavailable,
  canManage,
  currentUserId,
  loadFailed,
  onOpenRoles,
}: AccessUsersListProps) {
  const { t } = useTranslations("settings");

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div
        className={cn(
          GRID_COLS,
          "border-b border-border bg-muted/40 px-3.5 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground",
        )}
      >
        <div>{t("access_col_person")}</div>
        <div>{t("access_col_roles")}</div>
        <div>{t("access_col_effective")}</div>
        {/* Named for assistive tech even though it is visually blank — a column of buttons with no header
            is announced as an unlabelled column. */}
        <span className="sr-only">{t("access_col_actions")}</span>
      </div>

      {users.length === 0 ? (
        <AccessUsersEmpty loadFailed={loadFailed} />
      ) : (
        users.map((u, i) => {
          const assigned = rolesByUser[u.id] ?? [];
          const summary = summaryById[u.id];
          const rolesFailed = rolesUnavailable.has(u.id);
          const noRoles = !rolesFailed && assigned.length === 0;
          const isMe = !!currentUserId && u.id === currentUserId;
          const isExternal = u.user_type === "organization_member";
          const isPending = u.status === "pending_verification";
          const openRolesDrawer = canOpenRolesFor(u.id, catalogUnavailable, rolesUnavailable)
            ? () => onOpenRoles(u)
            : undefined;

          return (
            <div
              key={u.id}
              className={cn(
                GRID_COLS,
                "px-3.5 py-3",
                i !== users.length - 1 && "border-b border-border",
                u.status === "suspended" && "opacity-55",
                isMe && "bg-primary/5",
              )}
            >
              {/* Identity */}
              <div className="flex min-w-0 items-center gap-2.5 pr-3">
                <PrincipalAvatar name={u.full_name} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[13px] font-medium text-foreground">{u.full_name}</span>
                    {isMe && <ColocaBadge variant="primary">{t("access_badge_you")}</ColocaBadge>}
                    {isExternal && (
                      <ColocaBadge variant="secondary">{t("access_badge_external")}</ColocaBadge>
                    )}
                    {isPending && (
                      <ColocaBadge variant="warning" icon={Clock}>
                        {t("access_badge_pending")}
                      </ColocaBadge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{u.email}</div>
                </div>
              </div>

              {/* Roles — pills capped; "+N" opens the same drawer as the row's action button */}
              <PrincipalRolesCell roles={assigned} rolesFailed={rolesFailed} onShowAll={openRolesDrawer} />

              {/* Effective */}
              <div>
                <AccessEffectiveCell
                  summary={summary}
                  noRoles={noRoles}
                  rolesFailed={rolesFailed}
                  catalogUnavailable={catalogUnavailable}
                />
              </div>

              {/* Action */}
              <div className="flex justify-end">
                {openRolesDrawer && (
                  <ColocaButton
                    variant="default"
                    size="sm"
                    icon={canManage ? Shield : Eye}
                    onClick={openRolesDrawer}
                  >
                    {canManage ? t("access_action_roles") : t("access_action_view")}
                  </ColocaButton>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
